/**
 * Dragon Shield CSV exporter
 */
import { LANGUAGE_LABELS } from '@/lib/cardUtils';
import { generateCSV } from '@/lib/csv';
import type { EnrichedCollectionCard } from '@/types/card';

interface DragonShieldExportRow {
  [key: string]: string | number;
  'Folder Name': string;
  Quantity: number;
  'Trade Quantity': number;
  'Card Name': string;
  'Set Code': string;
  'Set Name': string;
  'Card Number': string;
  Condition: string;
  Printing: string;
  Language: string;
  'Price Bought': string;
  'Date Bought': string;
}

/**
 * Map condition code to Dragon Shield format
 */
function mapCondition(condition: string): string {
  const map: Record<string, string> = {
    M: 'Mint',
    NM: 'NearMint',
    LP: 'Good',
    MP: 'LightPlayed',
    HP: 'Played',
    D: 'Poor',
  };
  return map[condition] ?? 'NearMint';
}

/**
 * Map finish to Dragon Shield printing
 */
function mapPrinting(finish: string): string {
  if (finish === 'foil') return 'Foil';
  if (finish === 'etched') return 'Foil'; // Dragon Shield doesn't distinguish etched
  return 'Normal';
}

/**
 * Export to Dragon Shield CSV format
 */
export function exportDragonShield(cards: EnrichedCollectionCard[], folderNames: Map<string, string>): string {
  const rows: DragonShieldExportRow[] = cards.map((item) => ({
    'Folder Name': item.folder_id ? (folderNames.get(item.folder_id) ?? '') : '',
    Quantity: item.quantity,
    'Trade Quantity': 0,
    'Card Name': item.card.name,
    'Set Code': item.card.set.toUpperCase(),
    'Set Name': item.card.set_name,
    'Card Number': item.card.collector_number,
    Condition: mapCondition(item.condition),
    Printing: mapPrinting(item.finish),
    Language: LANGUAGE_LABELS[item.language] ?? 'English',
    'Price Bought': item.purchase_price?.toFixed(2) ?? '',
    'Date Bought': item.purchase_date ?? '',
  }));

  const columns: { key: keyof DragonShieldExportRow; header: string }[] = [
    { key: 'Folder Name', header: 'Folder Name' },
    { key: 'Quantity', header: 'Quantity' },
    { key: 'Trade Quantity', header: 'Trade Quantity' },
    { key: 'Card Name', header: 'Card Name' },
    { key: 'Set Code', header: 'Set Code' },
    { key: 'Set Name', header: 'Set Name' },
    { key: 'Card Number', header: 'Card Number' },
    { key: 'Condition', header: 'Condition' },
    { key: 'Printing', header: 'Printing' },
    { key: 'Language', header: 'Language' },
    { key: 'Price Bought', header: 'Price Bought' },
    { key: 'Date Bought', header: 'Date Bought' },
  ];

  // Add sep=, header for Excel compatibility
  return '"sep=,"\n' + generateCSV(rows, columns);
}
